import moment from "moment";

import Page from "@/components/Page";
import {
	DsBar,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsPreviewNotice,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const SAMPLE_VENUES = [
	"Campo A (erba naturale)",
	"Campo B (sintetico)",
	"Campo C (calcio a 7)",
];
const SAMPLE_OCCUPANCY = [
	[90, 95, 85, 100, 90, 60, 40],
	[100, 100, 95, 100, 100, 80, 30],
	[70, 60, 75, 65, 80, 100, 20],
];

const SAMPLE_MAINTENANCE = [
	{
		id: 1,
		item: "Rigenerazione manto Campo A",
		when: "Entro 15 novembre",
		status: "warn",
	},
	{
		id: 2,
		item: "Sostituzione fari torre 3",
		when: "Programmata 4 novembre",
		status: "ok",
	},
	{
		id: 3,
		item: "Caldaia spogliatoi settore giovanile",
		when: "Guasto segnalato",
		status: "alert",
	},
];

const SAMPLE_LOGISTICS = [
	{
		id: 1,
		item: "Pulmino 9 posti",
		detail: "Revisione a dicembre",
		status: "ok",
	},
	{
		id: 3,
		item: "Magazzino kit gara",
		detail: "Mancano 12 completi taglia S",
		status: "warn",
	},
];

const occupancyTone = (v) => (v >= 95 ? "red" : v >= 80 ? "yellow" : "green");

function DsFacilitiesPage(props) {
	const {handleNotification} = props;
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const venueNames = (s?.venues || []).map((v) => v.name).filter(Boolean);
	// Trasferte reali dei prossimi 14 giorni; lo stato organizzativo è d'esempio
	const trips = (s?.teams || [])
		.flatMap((t) =>
			t.upcoming
				.filter(
					(m) =>
						m.awayRosterId === t.id &&
						moment(m.startDate).isBefore(moment().add(14, "days")),
				)
				.map((m) => ({
					id: "trip-" + m.id + t.id,
					date: m.startDate,
					item: `Trasferta a ${m.homeClub} · ${t.name}`,
					detail: `${moment(m.startDate).format("ddd D MMM HH:mm")} · pullman da confermare`,
					status:
						moment(m.startDate).diff(moment(), "days") <= 3 ? "alert" : "warn",
				})),
		)
		.sort((a, b) => new Date(a.date) - new Date(b.date));
	const names = venueNames.length ? venueNames.slice(0, 6) : SAMPLE_VENUES;
	const occupancy = names.map((name, i) => ({
		id: name,
		name,
		days: SAMPLE_OCCUPANCY[i % SAMPLE_OCCUPANCY.length],
	}));

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Strutture & logistica"
					subtitle="Campi, manutenzioni, trasporti e magazzino"
					snapshot={s}
					scope={scope}
					onReload={reload}
					reloading={loading}
				/>
				<DsPreviewNotice>
					Area in arrivo: i campi sono quelli censiti nel sistema, occupazione,
					manutenzioni e logistica sono dati di esempio.
				</DsPreviewNotice>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsKpiRow>
							<DsKpi
								label="Campi"
								value={venueNames.length || "—"}
								sub="censiti"
							/>
							<DsKpi
								label="Occupazione media"
								value="78%"
								sub="settimana in corso"
							/>
							<DsKpi label="Manutenzioni aperte" value="3" tone="yellow" />
							<DsKpi
								label="Trasferte da organizzare"
								value={trips.length}
								sub="prossimi 14 giorni"
							/>
						</DsKpiRow>

						<DsSection title="Occupazione campi" preview>
							<DsTable
								columns={[
									{key: "name", label: "Campo", render: (r) => <b>{r.name}</b>},
									...DAYS.map((d, i) => ({
										key: d,
										label: d,
										render: (r) => (
											<div title={r.days[i] + "%"}>
												<DsBar
													value={r.days[i]}
													max={100}
													tone={occupancyTone(r.days[i])}
												/>
											</div>
										),
									})),
								]}
								rows={occupancy}
							/>
						</DsSection>

						<div className="ds-two-cols">
							<DsSection title="Manutenzioni" preview>
								<div className="ds-list">
									{SAMPLE_MAINTENANCE.map((m) => (
										<div key={m.id} className="ds-list__item">
											<div className="ds-list__main">
												<div className="ds-list__title">{m.item}</div>
												<div className="ds-list__sub">{m.when}</div>
											</div>
											<DsStatus status={m.status} />
										</div>
									))}
								</div>
							</DsSection>
							<DsSection title="Trasporti e magazzino" preview>
								<div className="ds-list">
									{[...trips, ...SAMPLE_LOGISTICS].map((m) => (
										<div key={m.id} className="ds-list__item">
											<div className="ds-list__main">
												<div className="ds-list__title">{m.item}</div>
												<div className="ds-list__sub">{m.detail}</div>
											</div>
											<DsStatus status={m.status} />
										</div>
									))}
								</div>
							</DsSection>
						</div>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsFacilitiesPage;
